import { useConfiguration } from "../hooks/useConfiguration";
import {
    PricesSection,
    ReferralSection,
    TierLimitsSection,
    BoosterPacksSection,
    ActiveSaleSection,
    AdsgramSection,
    SystemSettingsSection,
    GamificationSection,
} from "../components/config/ConfigSections";
import { PageHeader } from "../components/PageHeader";

export function ConfigurationView() {
    const {
        config,
        loading,
        savingSection,
        savedSection,
        error,
        isNew,
        hasChanges,
        saveSection,
        updateConfig,
        updateReferral,
        updateBooster,
        addBooster,
        removeBooster,
        updateTier,
        updateSale,
        updateAdsgram,
        updateXPRewards,
        updateDailyLoginDay,
        addDailyLoginDay,
        removeDailyLoginDay,
    } = useConfiguration();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-pulse text-lg text-gray-400">Loading configuration...</div>
            </div>
        );
    }

    const commonProps = {
        config,
        isNew,
    };

    // Helper to wrap saveSection for each component
    const createSaveHandler = (sectionName: string) => (data: any) => saveSection(sectionName, data);

    return (
        <div className="p-8 max-w-4xl mx-auto space-y-8 bg-gray-950 min-h-screen text-slate-200">
            <PageHeader
                title="Configuration"
                subtitle="Pricing, tier limits, and feature settings"
                rightContent={
                    isNew && (
                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full">
                            New configuration
                        </span>
                    )
                }
            />

            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl">
                    {error}
                </div>
            )}

            <PricesSection
                {...commonProps}
                onSave={createSaveHandler("prices")}
                saving={savingSection === "prices"}
                saved={savedSection === "prices"}
                hasChanges={hasChanges("prices")}
                updateConfig={updateConfig}
            />

            <ReferralSection
                {...commonProps}
                onSave={createSaveHandler("referrals")}
                saving={savingSection === "referrals"}
                saved={savedSection === "referrals"}
                hasChanges={hasChanges("referrals")}
                updateReferral={updateReferral}
            />

            <TierLimitsSection
                {...commonProps}
                onSave={createSaveHandler("tiers")}
                saving={savingSection === "tiers"}
                saved={savedSection === "tiers"}
                hasChanges={hasChanges("tiers")}
                updateTier={updateTier}
            />

            <BoosterPacksSection
                {...commonProps}
                onSave={createSaveHandler("boosters")}
                saving={savingSection === "boosters"}
                saved={savedSection === "boosters"}
                hasChanges={hasChanges("boosters")}
                updateBooster={updateBooster}
                addBooster={addBooster}
                removeBooster={removeBooster}
            />

            <ActiveSaleSection
                {...commonProps}
                onSave={createSaveHandler("sale")}
                saving={savingSection === "sale"}
                saved={savedSection === "sale"}
                hasChanges={hasChanges("sale")}
                updateSale={updateSale}
            />

            <AdsgramSection
                {...commonProps}
                onSave={createSaveHandler("adsgram")}
                saving={savingSection === "adsgram"}
                saved={savedSection === "adsgram"}
                hasChanges={hasChanges("adsgram")}
                updateAdsgram={updateAdsgram}
            />

            <GamificationSection
                {...commonProps}
                onSave={createSaveHandler("gamification")}
                saving={savingSection === "gamification"}
                saved={savedSection === "gamification"}
                hasChanges={hasChanges("gamification")}
                updateXPRewards={updateXPRewards}
                updateDailyLoginDay={updateDailyLoginDay}
                addDailyLoginDay={addDailyLoginDay}
                removeDailyLoginDay={removeDailyLoginDay}
            />

            <SystemSettingsSection
                {...commonProps}
                onSave={createSaveHandler("system")}
                saving={savingSection === "system"}
                saved={savedSection === "system"}
                hasChanges={hasChanges("system")}
                updateConfig={updateConfig}
            />
        </div>
    );
}
